import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { EkasiServeAppBar, equalObj, formatCurrency, formatNumber, isNumber, LoadingPage, parseNumber, selectAllOnFocus, SetState } from "./Common";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Snackbar from "@mui/material/Snackbar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { Supplier } from "./Suppliers";
import { Container, InputAdornment, TableFooter, TablePagination, TablePaginationActions } from "@mui/material";
import { Add, Edit } from "@mui/icons-material";

export interface Product {
    id: number;
	description: string;
	item_size: string;
	cost: number;
    costText: string;
	price: number;
    priceText: string;
	vat: number;
	supplier_id: number;
}

export function ProductsTable({ products, foundProducts, supplier, filterText, page }: { products: Array<Product>, foundProducts: Array<Product>, supplier: Partial<Supplier>, filterText: string, page: number }) {
    const newProductPath = supplier.id !== 0
        ? "/suppliers/" + supplier.id + "/products/0"
        : "/suppliers/0";
    const editProductPathPrefix = supplier.id !== 0
        ? "/suppliers/" + supplier.id + "/products/"
        : "/suppliers/";

    const navigate = useNavigate();
    const handlePageChange = useCallback((_: any, page: number) => {
        if (supplier.id !== 0) {
            navigate("/suppliers/" + supplier.id + "/products/page/" + page);
        }
    }, [navigate, supplier]);

    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="35%">Description</TableCell>
                                    <TableCell width="20%">Quantity</TableCell>
                                    <TableCell width="20%">Cost</TableCell>
                                    <TableCell width="20%">Price</TableCell>
                                    <TableCell width="5%">
                                        <Button variant="contained" component={Link} to={newProductPath} fullWidth>
                                            <Add />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(foundProducts.length === 0 ? products : foundProducts).map((row) => (
                                    filterText === "" || row.description.includes(filterText) 
                                    ?   <TableRow key={row.id}>
                                            <TableCell>{row.description}</TableCell>
                                            <TableCell>{row.item_size}</TableCell>
                                            <TableCell>{formatCurrency(row.cost)}</TableCell>
                                            <TableCell>{formatCurrency(row.price)}</TableCell>
                                            <TableCell>
                                                <Button variant="contained" component={Link} to={editProductPathPrefix + row.id} fullWidth>
                                                    <Edit />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    : <></>
                                ))}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TablePagination
                                        rowsPerPageOptions={[]}
                                        colSpan={5}
                                        count={foundProducts.length === 0 ? -1 : foundProducts.length}
                                        rowsPerPage={foundProducts.length === 0 ? 50 : foundProducts.length}
                                        page={page}
                                        slotProps={{
                                            select: {
                                            inputProps: {
                                                'aria-label': 'rows per page',
                                            },
                                            native: true,
                                            },
                                        }}
                                        onPageChange={handlePageChange}
                                        ActionsComponent={TablePaginationActions}
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </Paper>
            </Grid>
        </Grid>
    )
}

// function mergeProducts(old: Array<Product>, toAdd: Array<Product>): Array<Product> {
//     var result = [...old];
//     for (let q of toAdd) {
//         if (result.find((p) => p.id === q.id && p.description === q.description && p.item_size === q.item_size) === undefined) {
//             result.push(q);
//         }
//     }
//     return result;
// }

export const ProductsPage = () => {
    const { supplierIdText, pageIdText } = useParams();
    const [products, setProducts] = useState<Array<Product>>([]);
    const [foundProducts, setFoundProducts] = useState<Array<Product>>([]);
    const [supplier, setSupplier] = useState<Partial<Supplier>>({});
    const [filterText, setFilterText] = useState("");
    const [snackbar, setSnackbar] = useState("");
    const limit = 50;
    const scrollLimitFound = useRef(false);

    const supplierId = parseInt(supplierIdText || "0");
    const pageId = parseInt(pageIdText || "0");

    useEffect(() => {
        const fetchProducts = async () => {
            if (supplierId !== 0) {
                const supplierResponse = await fetch("/api/v1/suppliers/" + supplierId);
                const supplierData = await supplierResponse.json();
                setSupplier(supplierData.result || {});
                if (supplierData.error) {
                    setSnackbar(supplierData.error);
                }
            } else {
                setSupplier({});
            }

            const productResponse = await fetch("/api/v1/products?supplier_id=" + supplierId + `&limit=${limit}&offset=${pageId * limit}`);
            const productData = await productResponse.json();
            if ((productData.result || []).length === 0) {
                scrollLimitFound.current = true;
            }
            setProducts(productData.result || []);
            if (productData.error) {
                setSnackbar(productData.error);
            }
        };
        fetchProducts();
    }, [supplierId, pageId]);

    const findProducts = async () => {
        if (filterText === "") {
            setFoundProducts([]);
        } else {
            const response = await fetch(`/api/v1/products?supplier=${supplierId}&description=` + encodeURIComponent(filterText));
            const data = await response.json();
            setFoundProducts(data.result || []);
            if (data.error) {
                setSnackbar(data.error);
            }
        }
    }

    return (
        <Stack spacing={0}>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={8}> <h1> {supplier.name} </h1> </Grid>
                <Grid size={4}> 
                    <TextField
                        id="filterText"
                        label="Search"
                        variant="outlined"
                        onChange={(e) => { setFilterText(e.target.value); setFoundProducts([]); }}
                        onKeyUp={e => e.key === "Enter" ? findProducts() : 0}
                        fullWidth
                        value={filterText} />
                </Grid>
                <Grid size={12}>
                    <ProductsTable products={products} foundProducts={foundProducts} supplier={supplier} filterText={filterText} page={pageId}/>
                </Grid>
            </Grid>
            <Grid size={12} sx={{ paddingTop: 8 }}> </Grid>
            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
        </Stack>
    )
}

export function ProductForm({ product, setProduct }: { product: Partial<Product>, setProduct: SetState<Partial<Product>>}) {
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    id="description"
                    label="Description"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setProduct(product => ({ ...product, description: e.target.value }))}
                    fullWidth
                    value={product.description || ""} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    id="size"
                    label="Item Size"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setProduct(product => ({ ...product, item_size: e.target.value }))}
                    fullWidth
                    value={product.item_size || ""} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    id="cost"
                    label="Cost"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setProduct(product => ({ ...product, cost: parseNumber(e.target.value), costText: e.target.value })) }
                    error={!isNumber(product.costText || "")}
                    fullWidth
                    value={product.costText || ""} 
                    slotProps={{
                        input: {
                            startAdornment: <InputAdornment position="start">R</InputAdornment>
                        }
                    }}/>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    id="price"
                    label="Price"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setProduct(product => ({ ...product, price: parseNumber(e.target.value), priceText: e.target.value }))}
                    error={!isNumber(product.priceText || "")}
                    fullWidth
                    value={product.priceText || ""} 
                    slotProps={{
                        input: {
                            startAdornment: <InputAdornment position="start">R</InputAdornment>
                        }
                    }}/>
            </Grid>
            <Grid size={12}>
                <FormGroup>
                    <FormControlLabel
                        checked={product.vat === 1}
                        control={<Checkbox />}
                        onChange={(e, checked) => setProduct(product => ({ ...product, vat: checked ? 1 : 0 }))}
                        label="Product Has VAT" />
                </FormGroup>
            </Grid>
        </Grid>
    )
}

export const ProductPage = () => {
    const { supplierIdText, productIdText } = useParams();
    const [product, setProduct] = useState<Partial<Product>>({});
    const [snackbar, setSnackbar] = useState("");
    const [requestDelete, setRequestDelete] = useState(false);
    const [supplier, setSupplier] = useState<Partial<Supplier>>({});
    const [edited, setEdited] = useState(false);
    const [savedProduct, setSavedProduct] = useState<Partial<Product>>({});
    const [isLoading, setIsLoading] = useState(0);
    const navigate = useNavigate();

    const supplierId = parseInt(supplierIdText || "0");
    const productId = parseInt(productIdText || "0");

    useEffect(() => {
        const fetchData = async () => {
            const productResponse = await fetch("/api/v1/products/" + productId);
            const productData = await productResponse.json();
            productData.result.supplier_id = supplierId;
            productData.result.costText = formatNumber(productData.result.cost);
            productData.result.priceText = formatNumber(productData.result.price);
            setProduct(productData.result || {});
            setSavedProduct(productData.result || {});
            if (productData.error) {
                setSnackbar(productData.error);
            }

            const supplierResponse = await fetch("/api/v1/suppliers/" + supplierId);
            const supplierData = await supplierResponse.json();
            setSupplier(supplierData.result || {});
            if (supplierData.error) {
                setSnackbar(supplierData.error);
            }
            setIsLoading(lock => lock - 1);
        }
        setIsLoading(lock => lock + 1);
        fetchData();
    }, [productId, supplierId]);

    useEffect(() => {
        setEdited(!equalObj(product, savedProduct));
    }, [product, savedProduct]);

    const saveProduct = useCallback(async () => {
        const response = await fetch("/api/v1/products", {
            method: product.id === 0 ? "POST" : "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(product)
        });
        const data = await response.json();
        data.result.costText = formatNumber(data.result.cost);
        data.result.priceText = formatNumber(data.result.price);
        setProduct(data.result || {});
        setSavedProduct(data.result || {});
        if (data.error) {
            setSnackbar(data.error);
        } else {
            if (productId === 0) {
                navigate(`/suppliers/${supplier.id}/products/` + data.result.id, { replace: true });
            }
            setSnackbar("Information Saved");
        }
    }, [product, supplier, productId, navigate]);

    const deleteProduct = useCallback(async () => {
        const response = await fetch("/api/v1/products/" + product.id, { method: "DELETE" });
        const data = await response.json();
        if (data.error) {
            setSnackbar(data.error);
        } else {
            if (data.result) {
                data.result.costText = formatNumber(data.result.cost);
                data.result.priceText = formatNumber(data.result.price);
            }
            setProduct(data.result || {});
            navigate(-1);
        }
    }, [navigate, product.id]);

    if (isLoading) return (<LoadingPage />);

    return (
        <Stack spacing={0}>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={{ xs: 12, md: 8 }}> <h1> {supplier.name} Product </h1> </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                    {((product.id || 0) === 0)
                        ? <Button variant="contained" onClick={() => navigate(-1)} fullWidth> Cancel </Button>
                        : <Button variant="contained" onClick={() => setRequestDelete(true)} color="error" fullWidth> Delete </Button>
                    }
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                    <Button disabled={!edited} variant="contained" onClick={saveProduct} fullWidth> Save </Button>
                </Grid>
                <Grid size={12}>
                    <ProductForm product={product} setProduct={setProduct} />
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
            <Dialog open={requestDelete}>
                <DialogTitle> Are you sure you want to delete this product? </DialogTitle>
                <Button onClick={deleteProduct}> Yes, Delete </Button>
                <Button onClick={() => setRequestDelete(false)}> No, Cancel </Button>
            </Dialog>
        </Stack>

    )
}